import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";
import { EmptyState } from "@/components/layout/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface ScratchObjectRow {
  key: string;
  size_bytes: number;
  last_modified: string;
}

interface RunScratchResponse {
  run_id: string;
  run_status: string;
  retention_days: number;
  available: boolean;
  reason: string | null;
  vcm_job_id: string | null;
  objects: ScratchObjectRow[];
}

/**
 * Raw-scratch panel (U7.2, D71(i)) — the per-node VCM job objects
 * (`manifest.json`, `task.log`, `fragment_set.json` and siblings) behind
 * this run's dispatched job, reachable here rather than only via an S3
 * console or a support request. One-shot fetch, no polling: unlike Logs,
 * this data cannot change once the run is terminal (nothing here is
 * produced until the job has finished) short of VCM's own retention reaper
 * running, which this panel does not observe live either way.
 *
 * `retention_days` always renders, even when the live listing itself is
 * unavailable (still running, no VCM job on this run, or the listing call
 * failed) — the one number a user needs to know how long these files will
 * survive.
 */
export function RunScratch({ runId }: { runId: string }): JSX.Element {
  const { data, isLoading } = useQuery<RunScratchResponse>({
    queryKey: ["runs", "scratch", runId],
    queryFn: async () =>
      apiFetch<RunScratchResponse>(`/api/runs/${encodeURIComponent(runId)}/scratch`),
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-4 w-full" />
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <EmptyState title="Raw scratch is unavailable." description="Could not reach the API." />
    );
  }

  return (
    <section className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">
        Configured to retain for {data.retention_days} day
        {data.retention_days === 1 ? "" : "s"} from job completion — a policy FlowStack requests,
        not a guarantee it enforces.
        {data.vcm_job_id ? (
          <>
            {" "}
            VCM job <span className="font-mono">{data.vcm_job_id}</span>.
          </>
        ) : null}
      </p>

      {!data.available ? (
        <EmptyState
          title="Raw scratch is not listable right now."
          description={data.reason ?? "No further detail was reported."}
        />
      ) : data.objects.length === 0 ? (
        <EmptyState
          title="No scratch objects found."
          description="The job's attempt prefix reported no objects."
        />
      ) : (
        <Table className="border-collapse">
          <TableHeader>
            <TableRow>
              <TableHead>Key</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Last modified</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.objects.map((o) => (
              <TableRow key={o.key}>
                <TableCell className="font-mono text-xs">
                  <a
                    href={`/api/runs/${encodeURIComponent(runId)}/scratch/${scratchObjectPath(o.key)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="underline decoration-dotted underline-offset-2 hover:text-foreground"
                  >
                    {o.key}
                  </a>
                </TableCell>
                <TableCell className="text-xs tabular-nums">{formatBytes(o.size_bytes)}</TableCell>
                <TableCell className="text-xs">{formatTimestamp(o.last_modified)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

/**
 * Encode a scratch object key for the URL path segment of
 * `/api/runs/{id}/scratch/{key}`, which the backend mounts on a `:path`
 * converter (`AtlasVCMClient`'s attempt prefix is flat in practice, but the
 * server route does not assume that). Encoding per-segment rather than the
 * whole key preserves any `/` the key carries as real path separators
 * instead of escaping them to `%2F`.
 */
function scratchObjectPath(key: string): string {
  return key.split("/").map(encodeURIComponent).join("/");
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
